import React, { createContext, useContext, useEffect, useState } from 'react'
import { IBulkFormValuesFields, IFMIBulkFormContext, IFMIBulkFormProvider, TDayPlaceholders } from './interface'
import moment from 'moment'
import { DATE_FORMAT } from '@utils/constants'
import { getDaysOptions } from './constants'
import PortalProgram from '@classes/Program'
import { FMIStudentProfileContext, useFave } from '../../context'
import { InstructorSearchResultInfo, GetSortedInstructorsByProgram, SortedInstructorInfo } from '@interfaces/Instructor'
import { useFMIBulkSelectInstructors } from '../SelectInstructors/useSelectInstructors'
import { useSelector } from 'react-redux'
import { StoreProps } from '@interfaces/StoreState'
import { usePortalFeatures } from '@components/RoleBasedView'
import { useLazyQuery } from '@apollo/react-hooks'
import { GET_SORTED_INSTRUCTORS_BY_PROGRAM } from '@queries/sortedInstructorsByProgram'
import { omit, orderBy } from 'lodash'
import { useRouter } from 'next/router'

export const FMIBulkFormContext = createContext<IFMIBulkFormContext | null>(null)

export const useFMIBulkFormHook = (Program: PortalProgram): IFMIBulkFormContext => {
  const router = useRouter()
  const { selectedInstructors, selectedDate } = useSelector(({ fmiBulkForm }: StoreProps) => ({
    selectedInstructors: fmiBulkForm.instructors,
    selectedDate: fmiBulkForm.selectedDate,
  }))
  const { userProfile, isBulkPage = false } = useContext(FMIStudentProfileContext)
  const { state } = usePortalFeatures({
    configId: 'flag::FMI-bulksearch',
  })
  const { rawInstructors = [] } = useFave()
  const { setSelectAll, setSelectedInstructorIds } = useFMIBulkSelectInstructors()
  const ProgramEnrollDate = moment.tz(Program.ProgramEnrollDate, userProfile.Timezone)
  const isBulkOnlyFeature =
    (state.allowed && (Program?.hasBulkOnlyFeature() || false) && !router.query.sessionId) || isBulkPage

  const [values, setValues] = useState<IBulkFormValuesFields>({
    PreferredStartDate: ProgramEnrollDate.isAfter(moment(), 'date')
      ? ProgramEnrollDate.format(DATE_FORMAT.BASIC)
      : null,
    Day: [
      {
        DayofWeek: null,
        StartTime: null,
      },
    ],
  })
  const [dayPlaceholders, setDayPlaceholders] = useState<TDayPlaceholders>([null])
  const [isDateDirty, setIsDateDirty] = useState(false)
  const [isFormPristine, setIsFormPristine] = useState(true)
  const [isFetching, setIsFetching] = useState(false)
  const [hasFetchError, setHasFetchError] = useState(false)
  const [fetchedInstructors, setFetchedInstructors] = useState<SortedInstructorInfo[]>([])
  const [Instructors, setInstructors] = useState<InstructorSearchResultInfo[]>([])
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(false)
  const [getSortedInstructorsByProgram, { data, loading, error }] = useLazyQuery<GetSortedInstructorsByProgram>(
    GET_SORTED_INSTRUCTORS_BY_PROGRAM,
    {
      fetchPolicy: 'no-cache',
    }
  )

  useEffect(() => {
    const sortedInstructors = orderBy(
      (data?.getSortedInstructorsByProgram || [])?.map(({ instructorInfo }) => instructorInfo),
      'Ranking',
      'asc'
    )?.slice(0, 20)
    setFetchedInstructors(sortedInstructors)
    setInstructors(
      sortedInstructors
        .filter(({ InstructorId: sortedId }) => rawInstructors.some(({ InstructorId }) => InstructorId === sortedId))
        .map(({ InstructorId: sortedId }) => rawInstructors?.find(({ InstructorId }) => InstructorId === sortedId)!)
    )
    setHasFetchError(false)
    setIsFetching(false)
  }, [data])

  useEffect(() => {
    setIsFetching(loading)
  }, [loading])

  useEffect(() => {
    setHasFetchError(typeof error !== 'undefined')
  }, [error])

  const showWarning = () =>
    values.PreferredStartDate !== null &&
    values.Day.length >= 1 &&
    values.Day.some(({ DayofWeek, StartTime }) => DayofWeek !== null && StartTime !== null)

  const isLastDayTimeItem = (index) => index === values.Day.length - 1

  const isAddButtonDisabled = () =>
    (values.Day.length === 1 && (values.Day[0].DayofWeek === null || values.Day[0].StartTime === null)) ||
    (values.Day.length > 1 &&
      values.Day.some(
        ({ DayofWeek, StartTime }) =>
          (DayofWeek === null && StartTime) ||
          (DayofWeek && StartTime === null) ||
          (DayofWeek === null && StartTime === null)
      )) ||
    isButtonsDisabled

  const isFindButtonDisabled = () => values.PreferredStartDate === null || isAddButtonDisabled() || isButtonsDisabled

  const onDateChange = (date: Date) => {
    setValues({
      ...values,
      PreferredStartDate: moment(date).format(DATE_FORMAT.BASIC),
    })
    setIsDateDirty(true)
  }

  const onDayChange = (DayofWeek: number, index: number) => {
    setValues({
      ...values,
      Day: values.Day.map((Day, DayIndex) => (DayIndex === index ? { ...Day, DayofWeek } : Day)),
    })
    setDayPlaceholders([
      ...dayPlaceholders.slice(0, index),
      getDaysOptions().find(({ value }) => value === DayofWeek)?.label,
      ...dayPlaceholders.slice(index + 1),
    ])
  }

  const onTimeChange = (StartTime: string, index: number) =>
    setValues({
      ...values,
      Day: values.Day.map((Day, DayIndex) => (DayIndex === index ? { ...Day, StartTime } : Day)),
    })

  const onAddClick = () => {
    setValues({
      ...values,
      Day: [
        ...values.Day,
        {
          DayofWeek: null,
          StartTime: null,
        },
      ],
    })
    setDayPlaceholders([...dayPlaceholders, null])
  }

  const onDeleteClick = (index: number) => {
    setValues({
      ...values,
      Day: [...values.Day.slice(0, index), ...values.Day.slice(index + 1)],
    })
    setDayPlaceholders([...dayPlaceholders.slice(0, index), ...dayPlaceholders.slice(index + 1)])
  }

  const onFindClick = () => {
    setSelectAll(false)
    setSelectedInstructorIds([])
    setHasFetchError(false)
    setIsFetching(true)
    setIsFormPristine(false)

    getSortedInstructorsByProgram({
      variables: {
        sortedInstructorsByProgram: {
          ...omit(values, 'Day'),
          Days: values.Day.map(({ DayofWeek, ...rest }) => ({
            ...rest,
            DayofWeek: DayofWeek! - 1,
          })),
          ContractEndDate: Program.ContractEndDate,
          TimeZone: userProfile.Timezone,
          ProgramId: Program.ProgramId,
          StudentId: userProfile.SFId,
        },
      },
    })
  }

  return {
    Program,
    values,
    dayPlaceholders,
    isDateDirty,
    maxDate: new Date(Program.ContractEndDate),
    showWarning,
    isLastDayTimeItem,
    isAddButtonDisabled,
    isFindButtonDisabled,
    onDateChange,
    onDayChange,
    onTimeChange,
    onAddClick,
    onDeleteClick,
    onFindClick,
    isFormPristine,
    isFetching,
    hasFetchError,
    fetchedInstructors,
    Instructors,
    setInstructors,
    selectedInstructors,
    selectedDate,
    isButtonsDisabled,
    setIsButtonsDisabled,
    isBulkOnlyFeature,
  }
}

export const FMIBulkFormProvider: React.FC<IFMIBulkFormProvider> = ({ children, Program }) => {
  return <FMIBulkFormContext.Provider value={useFMIBulkFormHook(Program)}>{children}</FMIBulkFormContext.Provider>
}

export const useFMIBulkForm = (): IFMIBulkFormContext => {
  const context = useContext(FMIBulkFormContext)
  if (context === null) {
    throw new Error('useFMIBulkForm must be used within a FMIBulkFormProvider')
  }
  return context
}
